import React from 'react';
import Form from 'react-bootstrap/Form';
import MASpinner from '../../common/MASpinner';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import { hospitalSingleUpdate } from '../../../lib/redux/actions/hospitals';
import { renderCheckbox, renderField } from '../../../lib/helper/form';
import { addToast } from '../../../lib/redux/actions/toast';
import { Row, Col } from 'react-bootstrap';
import JoditEditor from 'jodit-react';
import { JODIT_EDITOR_CONFIG } from '../../../lib/helper/general';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { requests } from '../../../lib/helper/agent';
import * as API_ROUTES from '../../../lib/routing/api';

const mapStateToProps = (state, props) => {
  const initialValues = {
    name: props.hospital ? props.hospital.name : '',
    billToHospital: props.hospital?.billToHospital ? props.hospital.billToHospital : null,
    useAutomatedChasing: props.hospital ? props.hospital.useAutomatedChasing : true,
  };
  return {
    // Only map the specific state we need, not all of state.hospitals
    // to avoid conflicts with HospitalTypeahead's state management
    isUpdatingHospital: state.hospitals.isUpdatingHospital,
    initialValues,
  };
};

const mapDispatchToProps = {
  hospitalSingleUpdate,
  addToast,
};

class HospitalForm extends React.Component {
  constructor(props) {
    super(props);
    const initialNotes = props.hospital ? props.hospital.notes : '';
    const billToHospital = props.hospital?.billToHospital;
    this.state = {
      notes: initialNotes,
      initialNotes: initialNotes,
      selectedBillToHospital: billToHospital ? [billToHospital] : [],
      initialBillToHospital: billToHospital ? [billToHospital] : [],
      isLoadingHospitals: false,
      hospitalOptions: [],
    };
    this.editorRef = React.createRef();
    this.setNotes = this.setNotes.bind(this);
    this.handleBillToHospitalSearch = this.handleBillToHospitalSearch.bind(this);
  }

  componentDidMount() {
    if (this.props.hospital?.notes) {
      this.setState({
        notes: this.props.hospital.notes,
        initialNotes: this.props.hospital.notes,
      });
    }
  }

  componentDidUpdate(prevProps) {
    // Update editor content when switching to a different hospital or when notes change
    if (
      prevProps.hospital?.id !== this.props.hospital?.id ||
      (prevProps.hospital?.notes !== this.props.hospital?.notes && this.props.hospital?.notes !== undefined)
    ) {
      const billToHospital = this.props.hospital?.billToHospital;
      this.setState({
        notes: this.props.hospital?.notes || '',
        initialNotes: this.props.hospital?.notes || '',
        selectedBillToHospital: billToHospital ? [billToHospital] : [],
        initialBillToHospital: billToHospital ? [billToHospital] : [],
      });
    }
  }

  setNotes(value) {
    this.setState({
      notes: value,
    });
  }

  hasNotesChanged() {
    return this.state.notes !== this.state.initialNotes;
  }

  hasBillToHospitalChanged() {
    const current = this.state.selectedBillToHospital;
    const initial = this.state.initialBillToHospital;

    // Both empty - no change
    if (current.length === 0 && initial.length === 0) {
      return false;
    }

    // Different lengths - changed
    if (current.length !== initial.length) {
      return true;
    }

    // Compare IDs
    return current[0]?.id !== initial[0]?.id;
  }

  async handleBillToHospitalSearch(query) {
    this.setState({ isLoadingHospitals: true });
    try {
      const currentHospitalId = this.props.hospital?.id;
      const filters = { name: query, pagination: false };
      const response = await requests.get(`${API_ROUTES.HOSPITALS}?${new URLSearchParams(filters).toString()}`);

      // Filter out current hospital to prevent circular reference
      const hospitals = (response['hydra:member'] || []).filter((h) => h.id !== currentHospitalId);

      this.setState({ hospitalOptions: hospitals, isLoadingHospitals: false });
    } catch (error) {
      console.error('Error loading hospitals:', error);
      this.setState({ isLoadingHospitals: false, hospitalOptions: [] });
    }
  }

  onSubmit = async (values) => {
    if (this.props.readOnly) {
      return;
    }

    const { hospitalSingleUpdate, addToast, hospital } = this.props;

    try {
      await hospitalSingleUpdate(
        {
          name: values.name,
          billToHospital: this.state.selectedBillToHospital?.length > 0 ? `/hospitals/${this.state.selectedBillToHospital[0].id}` : null,
          notes: this.state.notes,
          useAutomatedChasing: values.useAutomatedChasing,
        },
        hospital.id,
      );
      // Update initial values to mark as clean after successful save
      this.setState({
        initialNotes: this.state.notes,
        initialBillToHospital: this.state.selectedBillToHospital,
      });
      addToast('Hospital updated successfully', true, false);
    } catch (error) {
      addToast('Failed to update hospital', false, true);
      throw error;
    }
  };

  render() {
    const { handleSubmit, error, pristine, submitting, isUpdatingHospital, readOnly = false } = this.props;

    return (
      <div>
        <h2 className="crud__section-header">Hospital Details</h2>

        {error && <div className="alert alert-danger">{error}</div>}

        <Form onSubmit={handleSubmit(this.onSubmit)}>
          <Row>
            <Col xs={12}>
              <Field className="mb-3" name="name" label={`Hospital Name`} id="name" type="text" component={renderField} disabled={readOnly} required />
            </Col>
            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label>Bill To Hospital (Optional)</Form.Label>
                <AsyncTypeahead
                  id="billToHospital-typeahead"
                  isLoading={this.state.isLoadingHospitals}
                  labelKey="name"
                  minLength={2}
                  onSearch={this.handleBillToHospitalSearch}
                  options={this.state.hospitalOptions}
                  placeholder="Search for a hospital..."
                  selected={this.state.selectedBillToHospital}
                  onChange={(selected) => this.setState({ selectedBillToHospital: selected })}
                  disabled={readOnly}
                  clearButton
                  emptyLabel="No hospitals found"
                  promptText="Type to search for hospitals..."
                />
                <Form.Text className="text-muted font-italic">
                  <small>The Invoice-to Hospital for this Hospital - the Hospital name that will be shown on the Invoice PDF</small>
                </Form.Text>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <JoditEditor
                  ref={this.editorRef}
                  value={this.state.notes}
                  config={JODIT_EDITOR_CONFIG}
                  tabIndex={1}
                  onBlur={(newContent) => this.setNotes(newContent)}
                  onChange={(newContent) => this.setNotes(newContent)}
                  disabled={readOnly}
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Field
                className="mb-3"
                name="useAutomatedChasing"
                disabled={readOnly}
                label="Use Automated Invoice Chasing?"
                id="use-automated-chasing"
                component={renderCheckbox}
              />
            </Col>
          </Row>

          {!readOnly && (
            <>
              {isUpdatingHospital ? (
                <MASpinner msg={`Updating Hospital...`} />
              ) : (
                <button
                  type="submit"
                  disabled={(pristine && !this.hasNotesChanged() && !this.hasBillToHospitalChanged()) || submitting}
                  className="btn btn-primary">
                  Update Hospital
                </button>
              )}
            </>
          )}
        </Form>
      </div>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: 'HospitalForm',
    enableReinitialize: true,
  })(HospitalForm),
);
